<script setup lang="ts">
import {
  getCategoryRoute,
  getSmallestThumbnailUrl,
  getTranslatedProperty,
} from "@shopware/helpers";
import type { Schemas } from "#shopware";
type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const expandedIds = ref<string>("");
const lastSelectedId = ref<string | null>(null);

defineProps<{
  navigationElementChildren: Array<NavigationElement>;
  lastElement?: boolean;
}>();

const emits = defineEmits<{
  (
    e: "updateActiveClass",
    navigationId: string,
    parentId: string | undefined
  ): void;
  (e: "focusoutLastElement", lastElement: boolean): void;
  (e: "close-menu"): void;
}>();

const emitUpdateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  lastSelectedId.value = navigationId;
  emits("updateActiveClass", navigationId, parentId);
  emits("close-menu");
};

function isCollapsed(navigationelement: Schemas["Category"]): boolean {
  return expandedIds.value == navigationelement.id;
}

const toggleCollapse = (navigationElement: Schemas["Category"]) => {
  if (isCollapsed(navigationElement)) {
    expandedIds.value = "";
  } else {
    expandedIds.value = navigationElement.id;
  }
};
</script>

<template>
  <li
    v-for="(childElement, index) in navigationElementChildren"
    :key="childElement.id"
  >
    <div class="flex justify-between">
      <NuxtLink
        :to="formatLink(getCategoryRoute(childElement))"
        :target="
          childElement.externalLink || childElement.linkNewTab ? '_blank' : ''
        "
        :class="{
          'font-bold':
            childElement.activeClass || lastSelectedId === childElement.id,
        }"
        @click="emitUpdateActiveClass(childElement.id, childElement.parentId)"
        @focusout="
          lastElement === true && index === navigationElementChildren.length - 1
            ? emits('focusoutLastElement', true)
            : null
        "
      >
        <h5 v-if="childElement.level == 3">
          {{ getTranslatedProperty(childElement, "name") }}
        </h5>
        <span v-else> {{ getTranslatedProperty(childElement, "name") }}</span>
      </NuxtLink>
      <span
        v-if="childElement.children.length > 0"
        class="right-[10px] relative cursor-pointer"
        :class="{ 'lg:hidden': (childElement.level ?? 0) < 4 }"
        @click="toggleCollapse(childElement)"
      >
        <i
          class="fa-solid fa-angle-right submenu-arrow"
          :class="{
            'rotate-90':
              expandedIds == childElement.id ||
              (childElement as NavigationElement).activeClass,
          }"
        >
        </i>
      </span>
    </div>

    <!-- Recursive Navigation -->
    <ul
      v-if="childElement.children && childElement.children.length > 0"
      class="mt-2"
      :style="
        expandedIds == childElement.id ||
        (childElement as NavigationElement).activeClass
          ? 'display: flex;'
          : ''
      "
    >
      <LayoutTopNavigationRecursive
        :navigation-element-children="childElement.children"
        @update-active-class="
          emitUpdateActiveClass(childElement.id, childElement.parentId)
        "
      />
    </ul>
  </li>
</template>
